import Chip from '@mui/material/Chip';
import List from '@mui/material/List';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import ErrorIcon from '@mui/icons-material/Error';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import LinearProgress from '@mui/material/LinearProgress';
import React, { useState, useEffect, useRef } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircularProgress from '@mui/material/CircularProgress';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import './index.css';
import axios from '../../store/axios';

const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks - ensures each part meets S3's 5MB minimum
const MIN_MULTIPART_SIZE = 10 * 1024 * 1024; // 10MB minimum to use multipart (ensures at least 2 parts of 5MB each)

const AttachmentsUpload = ({ migrationKey, onUploadComplete, onStatusChange }) => {
    const [loading, setLoading] = useState(true);
    const [activeStep, setActiveStep] = useState(0);
    const [contentVersionFile, setContentVersionFile] = useState(null);
    const [contentVersionStatus, setContentVersionStatus] = useState('idle');
    const [contentVersionProgress, setContentVersionProgress] = useState(0);
    const [contentVersionError, setContentVersionError] = useState(null);
    const [contentVersionImporting, setContentVersionImporting] = useState(false);
    const [contentDocumentLinkFile, setContentDocumentLinkFile] = useState(null);
    const [contentDocumentLinkStatus, setContentDocumentLinkStatus] = useState('idle');
    const [contentDocumentLinkProgress, setContentDocumentLinkProgress] = useState(0);
    const [contentDocumentLinkError, setContentDocumentLinkError] = useState(null);
    const [contentDocumentLinkImporting, setContentDocumentLinkImporting] = useState(false);
    const [contentVersionDbStatus, setContentVersionDbStatus] = useState(null); // { status, stats, error }
    const [contentDocumentLinkDbStatus, setContentDocumentLinkDbStatus] = useState(null); // { status, stats, error }
    const [zipFiles, setZipFiles] = useState([]);
    const [zipStatus, setZipStatus] = useState('idle');
    const [importStatus, setImportStatus] = useState(null);
    const [error, setError] = useState(null);

    // Single polling ref to prevent multiple concurrent polls
    const pollingRef = useRef(null);

    const steps = [
        { number: 1, title: 'ContentVersion.csv', description: 'File metadata' },
        { number: 2, title: 'ContentDocumentLink.csv', description: 'Record relationships' },
        { number: 3, title: 'Content Versions ZIP Files', description: 'Binary files' }
    ];

    // Load existing import status on mount, cleanup polling on unmount
    useEffect(() => {
        loadImportStatus();
        return () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
                pollingRef.current = null;
            }
        };
    }, [migrationKey]);

    // Warn user before leaving page if upload is in progress
    useEffect(() => {
        const isUploading = zipFiles.some(f => f['status'] === 'uploading') ||
                           contentVersionStatus === 'uploading' ||
                           contentDocumentLinkStatus === 'uploading';

        const handleBeforeUnload = (e) => {
            if (isUploading) {
                e.preventDefault();
                e.returnValue = 'You have files uploading. Are you sure you want to leave?';
                return e.returnValue;
            }
        };

        if (isUploading) {
            window.addEventListener('beforeunload', handleBeforeUnload);
        }

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [zipFiles, contentVersionStatus, contentDocumentLinkStatus]);

    const loadImportStatus = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`migrations/${migrationKey}/import-status`);
            const data = res['data']['body'];
            console.log('Loaded import status:', data);

            if (data['importFiles'] && data['importFiles'].length > 0) {
                // Check which files are already uploaded
                const hasContentVersion = data['importFiles'].some(f => f['filename'] === 'ContentVersion.csv');
                const hasContentDocumentLink = data['importFiles'].some(f => f['filename'] === 'ContentDocumentLink.csv');
                const uploadedZips = data['importFiles'].filter(f => f['filename']?.toLowerCase().endsWith('.zip'));

                if (hasContentVersion) {
                    const cvFile = data['importFiles'].find(f => f['filename'] === 'ContentVersion.csv');
                    setContentVersionStatus('uploaded');
                    setContentVersionProgress(100);
                    setContentVersionFile({ name: 'ContentVersion.csv' });
                    setContentVersionDbStatus({ status: cvFile['status'], stats: cvFile['stats'], error: cvFile['error'] });
                }

                if (hasContentDocumentLink) {
                    const cdlFile = data['importFiles'].find(f => f['filename'] === 'ContentDocumentLink.csv');
                    setContentDocumentLinkStatus('uploaded');
                    setContentDocumentLinkProgress(100);
                    setContentDocumentLinkFile({ name: 'ContentDocumentLink.csv' });
                    setContentDocumentLinkDbStatus({ status: cdlFile['status'], stats: cdlFile['stats'], error: cdlFile['error'] });
                }

                if (uploadedZips.length > 0) {
                    setZipFiles(uploadedZips.map(f => ({
                        name: f['filename'],
                        s3Key: f['s3Key'],
                        status: 'uploaded',
                        progress: 100,
                        dbStatus: f['status'] || 'pending', // Database status: pending, processing, complete, error
                        dbError: f['error'] || null,
                        dbStats: f['stats'] || null,
                        processed: f['status'] === 'complete',
                        processing: f['status'] === 'processing',
                        processError: f['error'] || null,
                        processStats: f['stats'] || null
                    })));
                    setZipStatus(data['status'] === 'complete' ? 'complete' : data['status'] === 'processing' ? 'processing' : 'uploaded');
                }

                // Determine active step based on uploaded files
                // Stay on step 2 even if zips are uploaded so user can add more
                if (hasContentVersion && hasContentDocumentLink) setActiveStep(2);
                else if (hasContentVersion) setActiveStep(1);
            }

            setImportStatus(data);

            // Notify parent of current status
            if (onStatusChange) onStatusChange(data['status'] || 'pending');

            // If still processing (overall or any individual file), poll for updates
            const hasProcessing = data['importFiles']?.some(f => f['status'] === 'processing');
            if (data['status'] === 'processing' || hasProcessing) pollImportStatus();
        } catch (err) {
            console.error('Error loading import status:', err);
        } finally {
            setLoading(false);
        }
    };

    const uploadCSVFile = async (file, setStatus, setProgress, objectType) => {
        try {
            setStatus('uploading');
            console.log(`[UPLOAD] File size: ${file['size']} bytes, MIN_MULTIPART_SIZE: ${MIN_MULTIPART_SIZE}, should use simple: ${file['size'] < MIN_MULTIPART_SIZE}`);

            // Use simple upload for small files (under 5MB) - S3 multipart requires minimum 5MB per part
            if (file['size'] < MIN_MULTIPART_SIZE) {
                console.log(`[SIMPLE UPLOAD] File ${file['name']} is ${file['size']} bytes, using simple upload`);
                const formData = new FormData();
                formData.append('file', file, `${objectType}.csv`);

                const res = await axios.post(`migrations/${migrationKey}/upload-salesforce-export?filename=${objectType}.csv`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => { if (e['total']) setProgress(Math.round((e['loaded'] / e['total']) * 100)); } });
                console.log('Upload response:', res['data']);
                setProgress(100);
                setStatus('uploaded');
                return res['data']['body']['s3Key'];
            }

            // Use multipart upload for large files (>5MB)
            const initRes = await axios.post(`migrations/${migrationKey}/upload-salesforce-export/init`, { filename: `${objectType}.csv`, contentType: 'text/csv' });
            console.log('Init multipart response:', initRes['data']);

            const { uploadId, s3Key } = initRes['data']['body'];
            const parts = [];
            const totalChunks = Math.ceil(file['size'] / CHUNK_SIZE);

            for (let i = 0; i < totalChunks; i++) {
                const start = i * CHUNK_SIZE;
                const end = Math.min(start + CHUNK_SIZE, file['size']);
                const chunk = file.slice(start, end);
                const formData = new FormData();
                formData.append('file', chunk);

                const partRes = await axios.post(`migrations/${migrationKey}/upload-salesforce-export/part?uploadId=${uploadId}&s3Key=${s3Key}&partNumber=${i + 1}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                console.log('Part upload response:', partRes['data']);
                parts.push({ ETag: partRes['data']['body']['ETag'], PartNumber: partRes['data']['body']['PartNumber'] });
                setProgress(Math.round(((i + 1) / totalChunks) * 100));
            }

            const completeRes = await axios.post(`migrations/${migrationKey}/upload-salesforce-export/complete`, { uploadId, s3Key, parts, filename: `${objectType}.csv` });
            console.log('Complete upload response:', completeRes['data']);
            setStatus('uploaded');
            return s3Key;
        } catch (err) {
            console.error('Upload error:', err);
            setStatus('error');
            throw err;
        }
    };

    const uploadZipFile = async (file, index) => {
        try {
            setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'uploading' } : f));

            let s3Key;

            // Use simple upload for small files (under 5MB)
            if (file['size'] < MIN_MULTIPART_SIZE) {
                console.log(`ZIP file ${file['name']} is ${file['size']} bytes, using simple upload`);
                const formData = new FormData();
                formData.append('file', file);

                const res = await axios.post(`migrations/${migrationKey}/upload-salesforce-export?filename=${encodeURIComponent(file['name'])}`, formData, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress: (e) => { if (e['total']) setZipFiles(prev => prev.map((f, idx) => idx === index ? { ...f, progress: Math.round((e['loaded'] / e['total']) * 100) } : f)); } });
                console.log('ZIP simple upload response:', res['data']);
                s3Key = res['data']['body']['s3Key'];
            } else {
                // Use multipart upload for large files (>5MB)
                const initRes = await axios.post(`migrations/${migrationKey}/upload-salesforce-export/init`, { filename: file['name'], contentType: 'application/zip' });
                console.log('ZIP init multipart response:', initRes['data']);

                const { uploadId, s3Key: multipartS3Key } = initRes['data']['body'];
                s3Key = multipartS3Key;
                const parts = [];
                const totalChunks = Math.ceil(file['size'] / CHUNK_SIZE);

                for (let i = 0; i < totalChunks; i++) {
                    const start = i * CHUNK_SIZE;
                    const end = Math.min(start + CHUNK_SIZE, file['size']);
                    const chunk = file.slice(start, end);
                    const formData = new FormData();
                    formData.append('file', chunk);

                    const partRes = await axios.post(`migrations/${migrationKey}/upload-salesforce-export/part?uploadId=${uploadId}&s3Key=${s3Key}&partNumber=${i + 1}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                    parts.push({ ETag: partRes['data']['body']['ETag'], PartNumber: partRes['data']['body']['PartNumber'] });
                    const progress = Math.round(((i + 1) / totalChunks) * 100);
                    setZipFiles(prev => prev.map((f, idx) => idx === index ? { ...f, progress } : f));
                }

                await axios.post(`migrations/${migrationKey}/upload-salesforce-export/complete`, { uploadId, s3Key, parts, filename: file['name'] });
            }

            // Update status to uploaded, then immediately start processing
            setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'uploaded', progress: 100, s3Key, processing: true, dbStatus: 'processing' } : f));
            console.log(`[AUTO-PROCESS] Starting processing for ZIP: ${file['name']} (s3Key: ${s3Key})`);

            // Auto-start processing immediately after upload completes
            if (onStatusChange) onStatusChange('processing');
            axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key }).then(() => {
                console.log(`[AUTO-PROCESS] Processing triggered for: ${file['name']}`);
                pollImportStatus();
            }).catch(err => {
                console.error(`[AUTO-PROCESS] Error triggering processing for ${file['name']}:`, err);
                setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, processing: false, dbStatus: 'error', dbError: err['message'] } : f));
            });

            return s3Key;
        } catch (err) {
            console.error('ZIP upload error:', err);
            setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, status: 'error', error: err['message'] } : f));
            throw err;
        }
    };

    const handleContentVersionChange = async (event) => {
        const file = event['target']['files'][0];
        if (!file) return;
        if (file['name'].toLowerCase() !== 'contentversion.csv') return setContentVersionError(`Invalid file. Please select "ContentVersion.csv" (you selected "${file['name']}")`);

        setContentVersionFile(file);
        setContentVersionStatus('idle');
        setContentVersionProgress(0);
        setContentVersionError(null);
        // Notify parent we're uploading (not complete)
        if (onStatusChange) onStatusChange('uploading');

        try {
            await uploadCSVFile(file, setContentVersionStatus, setContentVersionProgress, 'ContentVersion');
            setActiveStep(1);
            // Auto-import after upload
            setContentVersionImporting(true);
            if (onStatusChange) onStatusChange('processing');
            const res = await axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key: `${migrationKey}/import/ContentVersion.csv` });
            console.log('[AUTO-IMPORT] ContentVersion response:', res['data']);
            pollImportStatus();
        } catch (err) {
            setContentVersionError(err['message']);
            setContentVersionImporting(false);
            if (onStatusChange) onStatusChange('error');
        }
    };

    const handleContentDocumentLinkChange = async (event) => {
        const file = event['target']['files'][0];
        if (!file) return;
        if (file['name'].toLowerCase() !== 'contentdocumentlink.csv') return setContentDocumentLinkError(`Invalid file. Please select "ContentDocumentLink.csv" (you selected "${file['name']}")`);

        setContentDocumentLinkFile(file);
        setContentDocumentLinkStatus('idle');
        setContentDocumentLinkProgress(0);
        setContentDocumentLinkError(null);
        // Notify parent we're uploading (not complete)
        if (onStatusChange) onStatusChange('uploading');

        try {
            await uploadCSVFile(file, setContentDocumentLinkStatus, setContentDocumentLinkProgress, 'ContentDocumentLink');
            setActiveStep(2);
            // Auto-import after upload
            setContentDocumentLinkImporting(true);
            if (onStatusChange) onStatusChange('processing');
            const res = await axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key: `${migrationKey}/import/ContentDocumentLink.csv` });
            console.log('[AUTO-IMPORT] ContentDocumentLink response:', res['data']);
            pollImportStatus();
        } catch (err) {
            setContentDocumentLinkError(err['message']);
            setContentDocumentLinkImporting(false);
            if (onStatusChange) onStatusChange('error');
        }
    };

    const handleZipFilesChange = (event) => {
        const files = Array.from(event['target']['files']);
        const invalidFiles = files.filter(f => !f['name'].toLowerCase().endsWith('.zip'));
        if (invalidFiles.length > 0) return setError(`Invalid files detected. Only .zip files are allowed: ${invalidFiles.map(f => f['name']).join(', ')}`);

        // Check for duplicate file names (already uploaded or pending)
        const existingNames = zipFiles.map(f => f['name'].toLowerCase());
        const duplicateFiles = files.filter(f => existingNames.includes(f['name'].toLowerCase()));
        if (duplicateFiles.length > 0) return setError(`Duplicate files detected. These files have already been added: ${duplicateFiles.map(f => f['name']).join(', ')}`);

        // Check for duplicates within the selected files themselves
        const selectedNames = files.map(f => f['name'].toLowerCase());
        const hasDuplicatesInSelection = selectedNames.length !== new Set(selectedNames).size;
        if (hasDuplicatesInSelection) return setError('You selected multiple files with the same name. Please select unique files only.');

        setError(null);
        // Append new files to existing uploaded files (keep already uploaded, add new as pending)
        const newFiles = files.map(f => ({ name: f['name'], size: f['size'], file: f, progress: 0, status: 'pending' }));
        setZipFiles(prev => [...prev.filter(f => f['status'] === 'uploaded'), ...newFiles]);
    };

    const uploadAllZipFiles = async () => {
        const pendingFiles = zipFiles.filter(f => f['status'] === 'pending');
        if (pendingFiles.length === 0) return;

        setZipStatus('uploading');
        // Notify parent we're uploading (not complete)
        if (onStatusChange) onStatusChange('uploading');
        try {
            for (let i = 0; i < zipFiles.length; i++) {
                if (zipFiles[i]['status'] !== 'pending') continue; // Skip already uploaded
                // Each file auto-processes after upload completes (inside uploadZipFile)
                await uploadZipFile(zipFiles[i]['file'], i);
            }
            // Processing is already triggered by uploadZipFile, just update status
            setZipStatus('processing');
        } catch (err) {
            setZipStatus('error');
            setError(err['message']);
        }
    };

    // Unified polling function - only one poll at a time
    const pollImportStatus = async () => {
        // If already polling, don't start another
        if (pollingRef.current) return;

        const doPoll = async () => {
            try {
                const res = await axios.get(`migrations/${migrationKey}/import-status`);
                const data = res['data']['body'];
                console.log('[POLL] Import status:', data);
                setImportStatus(data);

                // Update ContentVersion status
                const cvFile = data['importFiles']?.find(f => f['filename'] === 'ContentVersion.csv');
                if (cvFile) {
                    setContentVersionDbStatus({ status: cvFile['status'], stats: cvFile['stats'], error: cvFile['error'] });
                    if (cvFile['status'] === 'complete' || cvFile['status'] === 'error') {
                        setContentVersionImporting(false);
                        if (cvFile['status'] === 'error') setContentVersionError(cvFile['error']);
                    }
                }

                // Update ContentDocumentLink status
                const cdlFile = data['importFiles']?.find(f => f['filename'] === 'ContentDocumentLink.csv');
                if (cdlFile) {
                    setContentDocumentLinkDbStatus({ status: cdlFile['status'], stats: cdlFile['stats'], error: cdlFile['error'] });
                    if (cdlFile['status'] === 'complete' || cdlFile['status'] === 'error') {
                        setContentDocumentLinkImporting(false);
                        if (cdlFile['status'] === 'error') setContentDocumentLinkError(cdlFile['error']);
                    }
                }

                // Update ZIP files status
                if (data['importFiles']) {
                    setZipFiles(prev => prev.map(f => {
                        const dbFile = data['importFiles'].find(df => df['s3Key'] === f['s3Key']);
                        if (dbFile) {
                            return {
                                ...f,
                                dbStatus: dbFile['status'] || 'pending',
                                dbError: dbFile['error'] || null,
                                dbStats: dbFile['stats'] || null,
                                processing: dbFile['status'] === 'processing',
                                processed: dbFile['status'] === 'complete',
                                processError: dbFile['error'] || null,
                                processStats: dbFile['stats'] || null
                            };
                        }
                        return f;
                    }));
                }

                // Check if anything is still processing (from server status)
                const hasProcessing = data['importFiles']?.some(f => f['status'] === 'processing');

                // Check if CSV files are still pending (not yet complete or error) based on server data
                const cvFileStatus = data['importFiles']?.find(f => f['filename'] === 'ContentVersion.csv')?.status;
                const cdlFileStatus = data['importFiles']?.find(f => f['filename'] === 'ContentDocumentLink.csv')?.status;
                const csvStillPending = (cvFileStatus && cvFileStatus !== 'complete' && cvFileStatus !== 'error') ||
                                        (cdlFileStatus && cdlFileStatus !== 'complete' && cdlFileStatus !== 'error');

                // Notify parent of current status
                if (onStatusChange) onStatusChange(data['status'] || (hasProcessing ? 'processing' : 'pending'));

                if (data['status'] === 'complete') {
                    setZipStatus('complete');
                    setActiveStep(3);
                    // Ensure importing flags are cleared when complete
                    setContentVersionImporting(false);
                    setContentDocumentLinkImporting(false);
                    if (onUploadComplete) onUploadComplete(data);
                    stopPolling();
                } else if (data['status'] === 'error') {
                    setZipStatus('error');
                    setError(data['importError']);
                    // Ensure importing flags are cleared on error
                    setContentVersionImporting(false);
                    setContentDocumentLinkImporting(false);
                    stopPolling();
                } else if (hasProcessing || csvStillPending) {
                    // Continue polling every 10 seconds if anything is processing or CSV is still pending
                    pollingRef.current = setTimeout(doPoll, 10000);
                } else {
                    // Nothing processing on server, stop polling
                    stopPolling();
                }
            } catch (err) {
                console.error('[POLL] Error:', err);
                stopPolling();
            }
        };

        doPoll();
    };

    const stopPolling = () => {
        if (pollingRef.current) {
            clearTimeout(pollingRef.current);
            pollingRef.current = null;
        }
    };

    // Import ContentVersion.csv
    const importContentVersion = async () => {
        setContentVersionImporting(true);
        setContentVersionError(null);
        try {
            const res = await axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key: `${migrationKey}/import/ContentVersion.csv` });
            console.log('[IMPORT] ContentVersion response:', res['data']);
            pollImportStatus(); // Start unified polling
        } catch (err) {
            console.error('[IMPORT] ContentVersion error:', err);
            setContentVersionImporting(false);
            setContentVersionError(err['message'] || 'Import failed');
        }
    };

    // Import ContentDocumentLink.csv
    const importContentDocumentLink = async () => {
        setContentDocumentLinkImporting(true);
        setContentDocumentLinkError(null);
        try {
            const res = await axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key: `${migrationKey}/import/ContentDocumentLink.csv` });
            console.log('[IMPORT] ContentDocumentLink response:', res['data']);
            pollImportStatus(); // Start unified polling
        } catch (err) {
            console.error('[IMPORT] ContentDocumentLink error:', err);
            setContentDocumentLinkImporting(false);
            setContentDocumentLinkError(err['message'] || 'Import failed');
        }
    };

    // Process a single ZIP file
    const processSingleZip = async (index) => {
        const file = zipFiles[index];
        if (!file || !file['s3Key']) return;

        console.log(`[PROCESS] Starting processing for: ${file['name']} (s3Key: ${file['s3Key']})`);

        // Update file status to processing
        setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, processing: true, dbStatus: 'processing', processError: null } : f));

        try {
            const res = await axios.post(`migrations/${migrationKey}/process-salesforce-export`, { s3Key: file['s3Key'] });
            console.log('[PROCESS] Response:', res['data']);
            pollImportStatus(); // Start unified polling
        } catch (err) {
            console.error('[PROCESS] Error:', err);
            setZipFiles(prev => prev.map((f, i) => i === index ? { ...f, processing: false, processError: err['message'] || 'Processing failed' } : f));
        }
    };

    const formatFileSize = (bytes) => {
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        return (bytes / (1024 * 1024 * 1024)).toFixed(2) + ' GB';
    };

    const getStepStatus = (stepIndex) => {
        if (stepIndex === 0) {
            if (contentVersionStatus === 'uploaded') return 'completed';
            if (contentVersionStatus === 'uploading') return 'active';
            if (activeStep === 0) return 'active';
            return 'pending';
        }
        if (stepIndex === 1) {
            if (contentDocumentLinkStatus === 'uploaded') return 'completed';
            if (contentDocumentLinkStatus === 'uploading') return 'active';
            if (activeStep === 1) return 'active';
            if (activeStep > 1) return 'completed';
            return 'pending';
        }
        if (stepIndex === 2) {
            if (zipStatus === 'complete' || zipStatus === 'uploaded') return 'completed';
            if (zipStatus === 'uploading' || zipStatus === 'processing') return 'active';
            if (zipFiles.filter(f => f['status'] === 'uploaded').length > 0) return 'completed';
            if (activeStep === 2) return 'active';
            return 'pending';
        }
        return 'pending';
    };

    const getStepIcon = (status) => {
        if (status === 'completed') return <CheckCircleIcon />;
        return <RadioButtonUncheckedIcon />;
    };

    if (loading) return (
        <div className="attachments-upload">
            <div className="upload-title-row">
                <h3 className="upload-title">Upload Salesforce Data Export Files</h3>
            </div>
            <div className="upload-loading"><CircularProgress size={24} /> <span>Loading upload status...</span></div>
        </div>
    );

    return (
        <div className="attachments-upload">
            <div className="upload-title-row">
                <h3 className="upload-title">Upload Salesforce Data Export Files</h3>
                <a href="/help/salesforce-data-export" target="_blank" rel="noopener noreferrer" className="how-to-get-link">How to get these files?</a>
                {/* <a href="https://developer.salesforce.com/docs/atlas.en-us.dataLoader.meta/dataLoader/exporting_data.htm" target="_blank" rel="noopener noreferrer" className="how-to-get-link">How to get these files?</a> */}
            </div>
            <p className="upload-description">Upload your Salesforce Data Export files in order. Files will auto-upload and process when selected.</p>

            <div className="upload-container">
                <div className="steps-sidebar">
                    {steps.map((step, index) => {
                        const status = getStepStatus(index);
                        return (
                            <div key={step['number']} className="step-item">
                                {index < steps.length - 1 && <div className={`step-connector ${status === 'completed' ? 'completed' : ''}`} />}
                                <div className={`step-icon ${status}`}>{getStepIcon(status)}</div>
                                <div className="step-content">
                                    <span className={`step-title ${status}`}>{step['number']}. {step['title']}</span>
                                    <span className="step-subtitle">{step['description']}</span>

                                    {/* CSV file status for step 1 */}
                                    {index === 0 && contentVersionStatus === 'uploaded' && (
                                        <div className="step-csv-status">
                                            <span className="step-uploaded-file">✓ ContentVersion.csv</span>
                                            {/* Show database status */}
                                            {contentVersionDbStatus?.status === 'complete' && contentVersionDbStatus?.stats && (
                                                <Chip size="small" label={`${(contentVersionDbStatus.stats.contentVersions || 0).toLocaleString()} records`} color="success" variant="outlined" />
                                            )}
                                            {contentVersionDbStatus?.status === 'processing' && (
                                                <Chip size="small" label="Processing" icon={<CircularProgress size={12} />} color="primary" variant="outlined" />
                                            )}
                                            {contentVersionDbStatus?.status === 'error' && (
                                                <Chip size="small" label="Error" color="error" variant="outlined" title={contentVersionDbStatus?.error || ''} />
                                            )}
                                            <div className="step-csv-actions">
                                                {contentVersionImporting && <CircularProgress size={14} />}
                                                {/* Only show import button if not complete and not importing */}
                                                {!contentVersionImporting && contentVersionDbStatus?.status !== 'complete' && contentVersionDbStatus?.status !== 'processing' && (
                                                    <IconButton size="small" onClick={importContentVersion} title="Import CSV" style={{ padding: 4 }}>
                                                        <PlayArrowIcon style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* CSV file status for step 2 */}
                                    {index === 1 && contentDocumentLinkStatus === 'uploaded' && (
                                        <div className="step-csv-status">
                                            <span className="step-uploaded-file">✓ ContentDocumentLink.csv</span>
                                            {/* Show database status */}
                                            {contentDocumentLinkDbStatus?.status === 'complete' && contentDocumentLinkDbStatus?.stats && (
                                                <Chip size="small" label={`${(contentDocumentLinkDbStatus.stats.contentDocumentLinks || 0).toLocaleString()} records`} color="success" variant="outlined" />
                                            )}
                                            {contentDocumentLinkDbStatus?.status === 'processing' && (
                                                <Chip size="small" label="Processing" icon={<CircularProgress size={12} />} color="primary" variant="outlined" />
                                            )}
                                            {contentDocumentLinkDbStatus?.status === 'error' && (
                                                <Chip size="small" label="Error" color="error" variant="outlined" title={contentDocumentLinkDbStatus?.error || ''} />
                                            )}
                                            <div className="step-csv-actions">
                                                {contentDocumentLinkImporting && <CircularProgress size={14} />}
                                                {/* Only show import button if not complete and not importing */}
                                                {!contentDocumentLinkImporting && contentDocumentLinkDbStatus?.status !== 'complete' && contentDocumentLinkDbStatus?.status !== 'processing' && (
                                                    <IconButton size="small" onClick={importContentDocumentLink} title="Import CSV" style={{ padding: 4 }}>
                                                        <PlayArrowIcon style={{ fontSize: 16 }} />
                                                    </IconButton>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* ZIP files list for step 3 */}
                                    {index === 2 && zipFiles.filter(f => f['status'] === 'uploaded').length > 0 && (
                                        <div className="step-zip-list">
                                            {zipFiles.filter(f => f['status'] === 'uploaded').map((f, zipIdx) => {
                                                const actualIndex = zipFiles.findIndex(zf => zf['s3Key'] === f['s3Key']);
                                                return (
                                                    <div key={f['s3Key'] || zipIdx} className="step-zip-item">
                                                        <span className="step-zip-name" title={f['name']}>
                                                            {f['dbStatus'] === 'complete' ? '✓' : '•'} {f['name']}
                                                        </span>
                                                        {/* Show database status */}
                                                        {f['dbStatus'] === 'complete' && f['dbStats'] && (
                                                            <Chip size="small" label={`${(f['dbStats']['filesProcessed'] || 0).toLocaleString()} files`} color="success" variant="outlined" style={{ marginLeft: 4, height: 20, fontSize: 11 }} />
                                                        )}
                                                        {(f['dbStatus'] === 'processing' || f['processing']) && f['dbStatus'] !== 'complete' && (
                                                            <Chip size="small" label={f['dbStats']?.filesProcessed ? `${f['dbStats']['filesProcessed'].toLocaleString()} files` : 'Processing'} icon={<CircularProgress size={10} />} color="primary" variant="outlined" style={{ marginLeft: 4, height: 20, fontSize: 11 }} />
                                                        )}
                                                        {f['dbStatus'] === 'error' && !f['processing'] && (
                                                            <Chip size="small" label="Error" color="error" variant="outlined" title={f['dbError'] || ''} style={{ marginLeft: 4, height: 20, fontSize: 11 }} />
                                                        )}
                                                        <div className="step-zip-actions">
                                                            {/* Only show play button if idle (not complete, not processing, not error) */}
                                                            {!f['processing'] && f['dbStatus'] !== 'complete' && f['dbStatus'] !== 'processing' && f['dbStatus'] !== 'error' && (
                                                                <IconButton size="small" onClick={() => processSingleZip(actualIndex)} title="Process ZIP" style={{ padding: 4 }}>
                                                                    <PlayArrowIcon style={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="upload-area">
                    {activeStep === 0 && (
                        <div className="upload-box">
                            <h4 className="upload-box-title">Select ContentVersion.csv</h4>
                            <p className="upload-box-description">This file contains metadata about all files (Title, Extension, ContentDocumentId, etc.)</p>
                            <input accept=".csv" style={{ display: 'none' }} id="content-version-upload" type="file" onChange={handleContentVersionChange} disabled={contentVersionStatus === 'uploading' || contentVersionStatus === 'uploaded'} />
                            <label htmlFor="content-version-upload">
                                <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} disabled={contentVersionStatus === 'uploading' || contentVersionStatus === 'uploaded'} className="upload-button">
                                    {contentVersionStatus === 'uploading' ? 'Uploading...' : 'Select ContentVersion.csv'}
                                </Button>
                            </label>
                            {contentVersionStatus === 'uploading' && (
                                <div className="upload-progress">
                                    <LinearProgress variant="determinate" value={contentVersionProgress} />
                                    <p className="upload-progress-text">{contentVersionProgress}%</p>
                                </div>
                            )}
                            {contentVersionError && <Alert severity="error" className="upload-error">{contentVersionError}</Alert>}
                        </div>
                    )}

                    {activeStep === 1 && (
                        <div className="upload-box">
                            <h4 className="upload-box-title">Select ContentDocumentLink.csv</h4>
                            <p className="upload-box-description">This file contains relationships between files and Salesforce records (Accounts, Contacts, etc.)</p>
                            <input accept=".csv" style={{ display: 'none' }} id="content-document-link-upload" type="file" onChange={handleContentDocumentLinkChange} disabled={contentDocumentLinkStatus === 'uploading' || contentDocumentLinkStatus === 'uploaded'} />
                            <label htmlFor="content-document-link-upload">
                                <Button variant="contained" component="span" startIcon={<CloudUploadIcon />} disabled={contentDocumentLinkStatus === 'uploading' || contentDocumentLinkStatus === 'uploaded'} className="upload-button">
                                    {contentDocumentLinkStatus === 'uploading' ? 'Uploading...' : 'Select ContentDocumentLink.csv'}
                                </Button>
                            </label>
                            {contentDocumentLinkStatus === 'uploading' && (
                                <div className="upload-progress">
                                    <LinearProgress variant="determinate" value={contentDocumentLinkProgress} />
                                    <p className="upload-progress-text">{contentDocumentLinkProgress}%</p>
                                </div>
                            )}
                            {contentDocumentLinkError && <Alert severity="error" className="upload-error">{contentDocumentLinkError}</Alert>}
                        </div>
                    )}

                    {(activeStep === 2 || activeStep === 3) && (
                        <div className="upload-box">
                            <div className="upload-box-header">
                                <FolderZipIcon />
                                <h4 className="upload-box-title">Select ZIP Files</h4>
                            </div>
                            <p className="upload-box-description">Upload all ZIP files from your Salesforce Data Export. Only .zip files are allowed.</p>

                            {/* Show already uploaded files */}
                            {zipFiles.length > 0 && (
                                <div className="zip-files-list">
                                    <p className="zip-files-label">{zipFiles.filter(f => f['status'] === 'uploaded').length} file(s) uploaded:</p>
                                    <List dense>
                                        {zipFiles.map((f, index) => (
                                            <ListItem key={index} className="zip-file-item">
                                                <div className="zip-file-content">
                                                    <div className="zip-file-row">
                                                        <span className="zip-file-name">{f['name']}</span>
                                                        <div className="zip-file-info">
                                                            {f['size'] && <span className="zip-file-size">{formatFileSize(f['size'])}</span>}
                                                            {f['status'] === 'uploading' && <span>{f['progress']}%</span>}
                                                            {f['status'] === 'pending' && <RadioButtonUncheckedIcon fontSize="small" />}
                                                            {f['status'] === 'error' && <ErrorIcon color="error" fontSize="small" />}
                                                            {/* Database status chip */}
                                                            {f['dbStatus'] === 'complete' && f['dbStats'] && (
                                                                <Chip size="small" label={`${(f['dbStats']['filesProcessed'] || 0).toLocaleString()} files`} icon={<CheckCircleIcon />} color="success" variant="outlined" />
                                                            )}
                                                            {(f['dbStatus'] === 'processing' || f['processing']) && f['dbStatus'] !== 'complete' && <Chip size="small" label={f['dbStats']?.filesProcessed ? `${f['dbStats']['filesProcessed'].toLocaleString()} files` : 'Processing'} icon={<CircularProgress size={14} />} color="primary" variant="outlined" />}
                                                            {f['dbStatus'] === 'error' && !f['processing'] && <Chip size="small" label="Error" icon={<ErrorIcon />} color="error" variant="outlined" title={f['dbError'] || ''} />}
                                                            {/* Play icon to process - only show if idle (not complete, not processing, not error) */}
                                                            {f['status'] === 'uploaded' && f['s3Key'] && !f['processing'] && f['dbStatus'] !== 'processing' && f['dbStatus'] !== 'complete' && f['dbStatus'] !== 'error' && (
                                                                <IconButton size="small" onClick={() => processSingleZip(index)} title="Process ZIP" style={{ padding: 4 }}>
                                                                    <PlayArrowIcon style={{ fontSize: 16 }} />
                                                                </IconButton>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {f['status'] === 'uploading' && <LinearProgress variant="determinate" value={f['progress']} className="zip-file-progress" />}

                                                    {/* Processing stats from database */}
                                                    {f['dbStatus'] === 'processing' && f['dbStats'] && (
                                                        <div className="zip-file-stats">
                                                            <span>Files: {f['dbStats']['filesProcessed'] || 0}</span>
                                                        </div>
                                                    )}

                                                    {/* Completed stats from database */}
                                                    {f['dbStatus'] === 'complete' && f['dbStats'] && (
                                                        <div className="zip-file-stats success">
                                                            <span>✓ {f['dbStats']['filesProcessed'] || 0} files processed</span>
                                                        </div>
                                                    )}

                                                    {/* Error from database */}
                                                    {f['dbStatus'] === 'error' && f['dbError'] && (
                                                        <div className="zip-file-error">
                                                            <ErrorIcon fontSize="small" /> {f['dbError']}
                                                        </div>
                                                    )}
                                                </div>
                                            </ListItem>
                                        ))}
                                    </List>
                                </div>
                            )}

                            {/* Upload more files button */}
                            <input accept=".zip" style={{ display: 'none' }} id="zip-files-upload" type="file" multiple onChange={handleZipFilesChange} disabled={zipStatus === 'uploading' || zipStatus === 'processing'} />
                            <label htmlFor="zip-files-upload">
                                <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} disabled={zipStatus === 'uploading' || zipStatus === 'processing'} className="upload-button">
                                    {zipFiles.length > 0 ? 'Add More ZIP Files' : 'Select ZIP Files'}
                                </Button>
                            </label>

                            {/* Show upload button only for pending files */}
                            {zipFiles.some(f => f['status'] === 'pending') && (
                                <Button variant="contained" onClick={uploadAllZipFiles} disabled={zipStatus === 'uploading'} className="zip-upload-button">
                                    {zipStatus === 'uploading' ? 'Uploading...' : `Upload ${zipFiles.filter(f => f['status'] === 'pending').length} New ZIP Files`}
                                </Button>
                            )}
                        </div>
                    )}

                    {zipStatus === 'processing' && (
                        <Alert severity="info" className="status-message">
                            Processing files in background... This may take a while for large exports.
                            {importStatus && importStatus['importStats'] && (
                                <p className="status-stats">ContentVersions: {importStatus['importStats']['contentVersions'] || 0} | ContentDocuments: {importStatus['importStats']['contentDocuments'] || 0} | Files: {importStatus['importStats']['filesProcessed'] || 0}</p>
                            )}
                        </Alert>
                    )}

                    {zipStatus === 'complete' && (
                        <Alert severity="success" className="status-message">
                            Import complete!
                            {importStatus && importStatus['importStats'] && (
                                <p className="status-stats">Imported {importStatus['importStats']['contentVersions'] || 0} ContentVersions, {importStatus['importStats']['contentDocuments'] || 0} ContentDocuments, and processed {importStatus['importStats']['filesProcessed'] || 0} files.</p>
                            )}
                        </Alert>
                    )}

                    {error && (
                        <Alert severity="error" className="status-message">
                            {error}
                            <Button size="small" className="dismiss-button" onClick={() => setError(null)}>Dismiss</Button>
                        </Alert>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AttachmentsUpload;
